import React from "react";
import { z } from "zod";

import AddDealSidebarForm from "./add.deal.sidebar.form";
import { TDeal } from "@/models/deals.model";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createDealSchema as baseDealSchema } from "@/components/AddDealModal/add.deal.modal";
import { useSnackbar } from "notistack";
import { usePipelinesStore } from "@/store/pipelines/pipelines.store";
import { getErrorFromBackend } from "@/utils/errors";
import leadsService from "@/services/leads.service";
import { useDealsStore } from "@/store/deals/deals.store";
import { PHONE_LENGTH_BY_COUNTRY, type COUNTRY_CODE_ISO_2 } from "@/constants/countries.constants";

type Props = {
  open: boolean;
  onClose: () => void;
  leadId?: number;
  onDealCreated?: (deal: TDeal) => void;
};

// Lead association: two mutually exclusive modes
// - Existing: chooseExistingLeads=true → leadId required, firstName/email optional
// - Manual:   chooseExistingLeads=false → firstName & email required, leadId optional (created via API first)
// Phone is optional in all cases. Deal creation always requires leadId.

const existingLeadSchema = z.object({
  leadId: z.number({ required_error: "Please search a lead" }),
});

const manualLeadSchema = z.object({
  firstName: z.string().min(1, "Lead name is required").trim(),
  email: z.string().min(1, "Lead email is required").email("Please enter a valid email address"),
});

const createDealSchema = baseDealSchema
  .extend({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z
      .any()
      .transform((val) => {
        // Handle empty strings, null, undefined, empty objects
        if (!val || val === "" || (typeof val === "object" && Object.keys(val).length === 0)) {
          return undefined;
        }
        // If it's an object with a value property, extract it
        if (typeof val === "object" && val.value !== undefined) {
          val = String(val.value);
        } else if (typeof val !== "string") {
          // Convert to string
          val = String(val);
        }
        // Strip all non-digit characters (including +, -, spaces, parentheses)
        const digitsOnly = val.replace(/\D/g, "");
        return digitsOnly || undefined;
      })
      .refine((val) => !val || /^\d+$/.test(val), {
        message: "Phone number must contain only digits",
      })
      .optional(),

    phoneCountryCode: z
      .string()
      .length(2, "Invalid country code"),
    email: z.string().optional(),
    chooseExistingLeads: z.boolean(),
    leadId: z.number().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.leadId) return; // Lead pre-selected (e.g. opened from lead section)

    if (data.chooseExistingLeads) {
      const result = existingLeadSchema.safeParse({ leadId: data.leadId });
      if (!result.success) {
        result.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: issue.path }));
      }
      return;
    }
    if (!data.phone || !data.phoneCountryCode) return;

    const rule = PHONE_LENGTH_BY_COUNTRY[data.phoneCountryCode];

    if (!rule) return;

    if (
      data.phone.length < rule.min ||
      data.phone.length > rule.max
    ) {
      ctx.addIssue({
        path: ["phone"],
        message: `Phone number must be ${rule.min === rule.max
          ? `${rule.min}`
          : `${rule.min}-${rule.max}`} digits`,
        code: z.ZodIssueCode.custom,
      });
    }
    const result = manualLeadSchema.safeParse({
      firstName: data.firstName,
      email: data.email,
    });
    if (!result.success) {
      result.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: issue.path }));
    }
  });

export type TCreateDealFormData = z.infer<typeof createDealSchema>;


const AddDealSidebar: React.FC<Props> = ({ open, onClose, leadId, onDealCreated }) => {

  const { enqueueSnackbar } = useSnackbar();
  const currentPipelineId = usePipelinesStore((s) => s.currentPipelineId);
  const pipelines = usePipelinesStore((s) => s.entities);
  const currentPipeline = pipelines[currentPipelineId];
  const firstStatusId = currentPipeline?.status?.[0]?.id ?? 0;

  const defaultValues: TCreateDealFormData = {
    chooseExistingLeads: true,
    name: "",
    currency: "USD",
    tags: [],
    phoneCountryCode: "US",
    leadId: leadId,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    amount: undefined,
    pipeline: currentPipelineId || 0,
    status: firstStatusId,
  };

  const useFormReturn = useForm<TCreateDealFormData>({
    resolver: zodResolver(createDealSchema),
    defaultValues,
  });
  const { handleSubmit, reset } = useFormReturn;


  React.useEffect(() => {
    if (!open) {
      const currentPipeline = pipelines[currentPipelineId];
      const firstStatusId = currentPipeline?.status?.[0]?.id ?? 0;
      reset({ ...defaultValues, pipeline: currentPipelineId || 0, status: firstStatusId, leadId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId, currentPipelineId, pipelines]);

  const onValid = async (data: TCreateDealFormData) => {
    try {

      const {
        chooseExistingLeads,
        firstName,
        lastName,
        email,
        amount,
        phoneCountryCode,
        // ✅ optional
        phone,
        ...dealPayload
      } = data;

      let leadIdToUse = dealPayload.leadId;

      // Manual mode: create lead first via service, then use leadId for the deal
      if (!leadIdToUse && !chooseExistingLeads && firstName?.trim() && email?.trim()) {
        const leadData = {
          firstName: firstName.trim(),
          email: email.trim(),
          phone: phone?.trim() || undefined,
          phoneCountryCode: (phoneCountryCode || undefined) as COUNTRY_CODE_ISO_2 | undefined,
          ...(lastName?.trim() && { lastName: lastName.trim() }),
        };
        const newLead = await leadsService.createLead(leadData);
        leadIdToUse = newLead.id;
      }
      if (amount === undefined) {
        enqueueSnackbar("Amount is required", { variant: "error" });
        return;
      }
      if (!leadIdToUse) {
        enqueueSnackbar("A lead is required to create a deal", { variant: "error" });
        return;
      }

      const createdDeal = await useDealsStore
        .getState()
        .createDeal({ ...dealPayload, leadId: leadIdToUse, amount });

      enqueueSnackbar("Deal added successfully", { variant: "success" });
      if (onDealCreated) {
        onDealCreated(createdDeal);
      }
      reset({ ...defaultValues, leadId });
      onClose();
    } catch (e) {
      enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
    }
  };

  const onSubmit = handleSubmit(onValid);

  return (
    <FormProvider {...useFormReturn}>
      <AddDealSidebarForm open={open} onClose={onClose} onSubmit={onSubmit} leadId={leadId} />
    </FormProvider>
  );
};

export default AddDealSidebar;